import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../utils/axios';
import { useAuth } from '../contexts/AuthContext';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  AlertTriangle,
  Activity,
  CheckCircle,
  Eye,
  TrendingUp,
  Bell,
  Shield,
  Users,
  ChevronLeft,
  ChevronRight,
  Calendar,
  ClipboardCheck,
  Clock,
  ArrowRight,
  Building2,
  UserPlus,
  FileCheck,
  Database,
  Loader2,
} from 'lucide-react';
import { toast } from 'sonner';
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, Cell } from 'recharts';
import { getSeverityColorSoft, SEVERITY_COLORS_HEX } from '../utils/styleUtils';
import GeoHeatmap from './GeoHeatmap';

// Default dashboard config
const DEFAULT_CONFIG = {
  widgets_visibles: [
    'stats_alertas', 'stats_investigacion', 'stats_monitores', 'stats_clientes',
    'chart_alertas_tiempo', 'chart_alertas_severidad', 'chart_alertas_monitor',
    'geo_heatmap', 'alertas_recientes', 'calendario_mini', 'aprobaciones_pendientes',
    'solicitudes_onboarding', 'ultimas_ejecuciones'
  ],
  alertas_mostrar: 5,
  refresh_interval: 60,
  mostrar_bienvenida: true
};

// Colores para el gráfico de alertas por monitor
const MONITOR_COLORS = ['#3b82f6', '#8b5cf6', '#ec4899', '#f59e0b', '#10b981', '#6366f1', '#ef4444', '#14b8a6'];

// Componente Mini Calendario
const MiniCalendar = ({ date, eventos }) => {
  const year = date.getFullYear();
  const month = date.getMonth();
  
  // Primer día del mes (0=Domingo, 1=Lunes, etc.)
  const firstDay = new Date(year, month, 1).getDay();
  // Ajustar para que empiece en Lunes (0=Lunes)
  const startOffset = firstDay === 0 ? 6 : firstDay - 1;
  
  // Días en el mes
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  
  // Generar array de días
  const days = [];
  for (let i = 0; i < startOffset; i++) {
    days.push(null);
  }
  for (let i = 1; i <= daysInMonth; i++) {
    days.push(i);
  }
  
  const weekDays = ['L', 'M', 'X', 'J', 'V', 'S', 'D'];
  const today = new Date();
  const isCurrentMonth = today.getMonth() === month && today.getFullYear() === year;
  
  const getEventForDay = (day) => {
    if (!day) return null;
    const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    return eventos?.[dateStr] || null;
  };

  return (
    <div className="select-none">
      {/* Días de la semana */}
      <div className="grid grid-cols-7 gap-1 mb-2">
        {weekDays.map(d => (
          <div key={d} className="text-center text-xs font-medium text-slate-400 py-1">
            {d}
          </div>
        ))}
      </div>
      
      {/* Días del mes */}
      <div className="grid grid-cols-7 gap-1">
        {days.map((day, idx) => {
          const evento = getEventForDay(day);
          const isToday = isCurrentMonth && day === today.getDate();
          
          let bgClass = 'bg-slate-50 hover:bg-slate-100';
          if (evento) {
            if (evento.criticas > 0) {
              bgClass = 'bg-red-100 hover:bg-red-200';
            } else if (evento.altas > 0) {
              bgClass = 'bg-orange-100 hover:bg-orange-200';
            } else {
              bgClass = 'bg-blue-100 hover:bg-blue-200';
            }
          }
          
          return (
            <div
              key={idx}
              className={`relative aspect-square flex flex-col items-center justify-center rounded-md text-sm transition-colors ${
                day ? bgClass : ''
              } ${isToday ? 'ring-2 ring-blue-500' : ''}`}
            >
              {day && (
                <>
                  <span className={`font-medium ${isToday ? 'text-blue-600' : 'text-slate-700'}`}>
                    {day}
                  </span>
                  {evento && evento.total > 0 && (
                    <span className="text-[10px] font-semibold text-slate-600">
                      {evento.total}
                    </span>
                  )}
                </>
              )}
            </div>
          );
        })}
      </div>
      
      {/* Leyenda */}
      <div className="flex items-center justify-center gap-4 mt-3 text-xs text-slate-500">
        <span className="flex items-center gap-1">
          <span className="w-3 h-3 rounded bg-red-200"></span> Críticas
        </span>
        <span className="flex items-center gap-1">
          <span className="w-3 h-3 rounded bg-orange-200"></span> Altas
        </span>
        <span className="flex items-center gap-1">
          <span className="w-3 h-3 rounded bg-blue-200"></span> Otras
        </span>
      </div>
    </div>
  );
};

export const Dashboard = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [recentAlerts, setRecentAlerts] = useState([]);
  const [chartData, setChartData] = useState({
    trend: [],
    bySeverity: [],
    byStatus: [],
    byMonitor: []
  });
  const [dashboardConfig, setDashboardConfig] = useState(DEFAULT_CONFIG);
  const [calendarData, setCalendarData] = useState({ mes: null, anio: null, eventos: {} });
  const [calendarDate, setCalendarDate] = useState(new Date());
  const [pendingApprovals, setPendingApprovals] = useState([]);
  const [pendingApprovalCount, setPendingApprovalCount] = useState(0);
  const [onboardingCounts, setOnboardingCounts] = useState({});
  const [lastExecution, setLastExecution] = useState(null);
  const [cierrePeriodoData, setCierrePeriodoData] = useState(null);
  const [showCierreDialog, setShowCierreDialog] = useState(false);

  useEffect(() => {
    loadDashboardConfig();
  }, []);

  useEffect(() => {
    if (dashboardConfig) {
      loadDashboardData();
      loadChartData();
      loadCalendarData();
      loadPendingApprovals();
      loadOnboardingCounts();
      loadLastExecution();
      loadCierrePeriodoData();
      
      // Auto-refresh if configured
      if (dashboardConfig.refresh_interval > 0) {
        const interval = setInterval(() => {
          loadDashboardData();
          loadChartData();
          loadPendingApprovals();
          loadOnboardingCounts();
          loadLastExecution();
          loadCierrePeriodoData();
        }, dashboardConfig.refresh_interval * 1000);
        return () => clearInterval(interval);
      }
    }
  }, [dashboardConfig]);

  useEffect(() => {
    loadCalendarData();
  }, [calendarDate]);

  const loadDashboardConfig = async () => {
    try {
      const res = await api.get('/config/dashboard');
      if (res.data) {
        setDashboardConfig({ ...DEFAULT_CONFIG, ...res.data });
      }
    } catch (error) {
    }
  };

  const loadDashboardData = async () => {
    try {
      const [statsRes, alertsRes] = await Promise.all([
        api.get('/stats/dashboard'),
        api.get(`/alerts?limit=${dashboardConfig.alertas_mostrar || 5}`)
      ]);
      setStats(statsRes.data);
      setRecentAlerts(alertsRes.data);
    } catch (error) {
      toast.error('Error al cargar datos del dashboard');
    } finally {
      setLoading(false);
    }
  };

  const loadChartData = async () => {
    try {
      const [alertsRes, monitorRes] = await Promise.all([
        api.get('/alerts?limit=200'),
        api.get('/stats/alerts-by-monitor?limit=8')
      ]);
      const alerts = alertsRes.data;

      // Datos para gráfico de tendencia (últimos 30 días)
      const last30Days = [...Array(30)].map((_, i) => {
        const date = new Date();
        date.setDate(date.getDate() - (29 - i));
        return {
          date: date.toLocaleDateString('es-ES', { day: '2-digit', month: 'short' }),
          alertas: alerts.filter(a => {
            const alertDate = new Date(a.created_at);
            return alertDate.toDateString() === date.toDateString();
          }).length
        };
      });

      // Datos por severidad
      const severityCount = {};
      alerts.forEach(a => {
        severityCount[a.severidad] = (severityCount[a.severidad] || 0) + 1;
      });
      const bySeverity = Object.entries(severityCount).map(([severidad, count]) => ({
        severidad,
        cantidad: count
      }));

      setChartData({
        trend: last30Days,
        bySeverity,
        byStatus: [],
        byMonitor: monitorRes.data || []
      });
    } catch (error) {
      console.error('Error loading chart data:', error);
    }
  };

  const loadCalendarData = async () => {
    try {
      const mes = calendarDate.getMonth() + 1;
      const anio = calendarDate.getFullYear();
      const res = await api.get(`/stats/calendar-events?mes=${mes}&anio=${anio}`);
      setCalendarData(res.data);
    } catch (error) {
      console.error('Error loading calendar data:', error);
    }
  };

  const loadPendingApprovals = async () => {
    try {
      const [countRes, listRes] = await Promise.all([
        api.get('/approvals/pending-count'),
        api.get('/approvals?mis_pendientes=true&limit=5')
      ]);
      setPendingApprovalCount(countRes.data.count || 0);
      setPendingApprovals(listRes.data || []);
    } catch (error) {
      console.error('Error loading pending approvals:', error);
    }
  };

  const loadOnboardingCounts = async () => {
    try {
      const res = await api.get('/onboarding/requests/count');
      setOnboardingCounts(res.data || {});
    } catch (error) {
      console.error('Error loading onboarding counts:', error);
    }
  };

  const loadLastExecution = async () => {
    try {
      const res = await api.get('/monitor-notifications/executions/last');
      if (res.data && res.data.id) {
        setLastExecution(res.data);
      }
    } catch (error) {
      console.error('Error loading last execution:', error);
    }
  };

  const loadCierrePeriodoData = async () => {
    try {
      const res = await api.get('/cierre-periodo/dashboard');
      setCierrePeriodoData(res.data);
    } catch (error) {
      console.error('Error loading cierre periodo data:', error);
    }
  };

  const handleMarcarCierre = async (periodo, tipo, completo) => {
    try {
      await api.post('/cierre-periodo/marcar', {
        periodo,
        tipo,
        completo,
        comentarios: null
      });
      toast.success(`Período ${periodo} ${completo ? 'cerrado' : 'reabierto'}`);
      loadCierrePeriodoData();
    } catch (error) {
      toast.error('Error al actualizar estado del período');
    }
  };

  const handleCalendarPrevMonth = () => {
    setCalendarDate(prev => new Date(prev.getFullYear(), prev.getMonth() - 1, 1));
  };

  const handleCalendarNextMonth = () => {
    setCalendarDate(prev => new Date(prev.getFullYear(), prev.getMonth() + 1, 1));
  };

  // Helper function to check if widget is visible
  const isWidgetVisible = (widgetId) => {
    return dashboardConfig.widgets_visibles?.includes(widgetId);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full bg-slate-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-slate-600">Cargando dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 lg:p-8 h-full overflow-auto bg-slate-50">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          {dashboardConfig.mostrar_bienvenida && user && (
            <p className="text-lg text-slate-600 mb-1">
              Bienvenido, <span className="font-semibold text-slate-800">{user.nombre?.split(' ')[0]}</span>
            </p>
          )}
          <h1 className="text-3xl font-bold text-slate-900">
            Dashboard de Monitoreo
          </h1>
          <p className="text-slate-500 mt-1">
            Resumen de actividad y alertas
          </p>
        </div>
      </div>

      {/* Stats Grid - Conditionally render based on config */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4 mb-8">
        {/* Total Alertas - Always visible */}
        {isWidgetVisible('stats_alertas') && (
          <Card className="p-5 bg-white border-slate-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs uppercase tracking-wider font-medium text-slate-500 mb-1">
                  Total Alertas
                </p>
                <p className="text-2xl font-bold text-slate-900">
                  {stats?.total_alertas || 0}
                </p>
              </div>
              <div className="p-3 rounded-lg bg-slate-100">
                <AlertTriangle size={22} className="text-slate-600" />
              </div>
            </div>
          </Card>
        )}

        {/* Nuevas */}
        {isWidgetVisible('stats_alertas') && (
          <Card className="p-5 bg-red-50 border-red-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs uppercase tracking-wider font-medium text-red-700 mb-1">
                  Nuevas
                </p>
                <p className="text-2xl font-bold text-red-700">
                  {stats?.alertas_nuevas || 0}
                </p>
              </div>
              <div className="p-3 rounded-lg bg-red-100">
                <Bell size={22} className="text-red-600" />
              </div>
            </div>
          </Card>
        )}

        {/* En Investigación */}
        {isWidgetVisible('stats_investigacion') && (
        <Card className="p-5 bg-blue-50 border-blue-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs uppercase tracking-wider font-medium text-blue-700 mb-1">
                En Investigación
              </p>
              <p className="text-2xl font-bold text-blue-700">
                {stats?.alertas_en_investigacion || 0}
              </p>
            </div>
            <div className="p-3 rounded-lg bg-blue-100">
              <Eye size={22} className="text-blue-600" />
            </div>
          </div>
        </Card>
        )}

        {/* Cerradas */}
        {isWidgetVisible('stats_alertas') && (
        <Card className="p-5 bg-green-50 border-green-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs uppercase tracking-wider font-medium text-green-700 mb-1">
                Cerradas
              </p>
              <p className="text-2xl font-bold text-green-700">
                {stats?.alertas_cerradas || 0}
              </p>
            </div>
            <div className="p-3 rounded-lg bg-green-100">
              <CheckCircle size={22} className="text-green-600" />
            </div>
          </div>
        </Card>
        )}

        {/* Monitores Activos */}
        {isWidgetVisible('stats_monitores') && (
        <Card className="p-5 bg-purple-50 border-purple-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs uppercase tracking-wider font-medium text-purple-700 mb-1">
                Monitores Activos
              </p>
              <p className="text-2xl font-bold text-purple-700">
                {stats?.monitores_activos || 0}
              </p>
            </div>
            <div className="p-3 rounded-lg bg-purple-100">
              <Shield size={22} className="text-purple-600" />
            </div>
          </div>
        </Card>
        )}

        {/* Clientes */}
        {isWidgetVisible('stats_clientes') && (
        <Card className="p-5 bg-indigo-50 border-indigo-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs uppercase tracking-wider font-medium text-indigo-700 mb-1">
                Total Clientes
              </p>
              <p className="text-2xl font-bold text-indigo-700">
                {stats?.total_clientes || 0}
              </p>
            </div>
            <div className="p-3 rounded-lg bg-indigo-100">
              <Users size={22} className="text-indigo-600" />
            </div>
          </div>
        </Card>
        )}
      </div>

      {/* Widget Cierre de Período */}
      {cierrePeriodoData && (
        <Card className="p-5 mb-8 bg-gradient-to-r from-teal-50 to-cyan-50 border-teal-200">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-teal-100 rounded-lg">
                <Database size={22} className="text-teal-600" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-slate-900">Estado de Períodos</h3>
                <p className="text-sm text-slate-500">Cierre de datos y presentaciones</p>
              </div>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowCierreDialog(true)}
              className="text-teal-700 border-teal-300 hover:bg-teal-100"
            >
              Ver más
            </Button>
          </div>
          
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {cierrePeriodoData.periodos?.slice(0, 4).map((p) => {
              const meses = ['', 'Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'];
              const mes = parseInt(p.periodo.substring(4, 6));
              const año = p.periodo.substring(0, 4);
              const periodoLabel = `${meses[mes]} ${año}`;
              
              return (
                <div key={p.periodo} className="bg-white rounded-lg p-3 border border-slate-200">
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-medium text-slate-800">{periodoLabel}</span>
                    <Badge 
                      className={
                        p.estado === 'presentado' ? 'bg-green-100 text-green-700' :
                        p.estado === 'vencido' ? 'bg-red-100 text-red-700' :
                        p.estado === 'por_vencer' ? 'bg-amber-100 text-amber-700' :
                        p.estado === 'listo' ? 'bg-blue-100 text-blue-700' :
                        'bg-slate-100 text-slate-700'
                      }
                    >
                      {p.estado === 'presentado' ? 'Presentado' :
                       p.estado === 'vencido' ? 'Vencido' :
                       p.estado === 'por_vencer' ? 'Por vencer' :
                       p.estado === 'listo' ? 'Listo' :
                       p.estado === 'generado' ? 'Generado' : 'Pendiente'}
                    </Badge>
                  </div>
                  <div className="flex gap-2 mt-2">
                    <button
                      onClick={() => handleMarcarCierre(p.periodo, 'movimientos', !p.cierre.movimientos_completo)}
                      className={`flex-1 p-1.5 rounded text-xs font-medium transition-colors ${
                        p.cierre.movimientos_completo 
                          ? 'bg-green-500 text-white' 
                          : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                      }`}
                      title="Movimientos"
                    >
                      Mov
                    </button>
                    <button
                      onClick={() => handleMarcarCierre(p.periodo, 'padron', !p.cierre.padron_completo)}
                      className={`flex-1 p-1.5 rounded text-xs font-medium transition-colors ${
                        p.cierre.padron_completo 
                          ? 'bg-green-500 text-white' 
                          : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                      }`}
                      title="Padrón"
                    >
                      Pad
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </Card>
      )}

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
        {/* Tendencia de Alertas */}
        {isWidgetVisible('chart_alertas_tiempo') && (
        <Card className="p-6 bg-white border-slate-200">
          <h3 className="text-lg font-semibold text-slate-900 mb-4">
            Tendencia de Alertas (30 días)
          </h3>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={chartData.trend}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis 
                  dataKey="date" 
                  tick={{ fontSize: 11, fill: '#64748b' }}
                  tickLine={{ stroke: '#cbd5e1' }}
                />
                <YAxis 
                  tick={{ fontSize: 11, fill: '#64748b' }}
                  tickLine={{ stroke: '#cbd5e1' }}
                />
                <Tooltip 
                  contentStyle={{ 
                    backgroundColor: '#fff',
                    border: '1px solid #e2e8f0',
                    borderRadius: '8px',
                    boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)'
                  }}
                />
                <Line 
                  type="monotone" 
                  dataKey="alertas" 
                  stroke="#2563eb" 
                  strokeWidth={2}
                  dot={{ fill: '#2563eb', strokeWidth: 2 }}
                  activeDot={{ r: 6 }}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </Card>
        )}

        {/* Alertas por Severidad */}
        {isWidgetVisible('chart_alertas_severidad') && (
        <Card className="p-6 bg-white border-slate-200">
          <h3 className="text-lg font-semibold text-slate-900 mb-4">
            Alertas por Severidad
          </h3>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={chartData.bySeverity}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis 
                  dataKey="severidad" 
                  tick={{ fontSize: 11, fill: '#64748b' }}
                />
                <YAxis 
                  tick={{ fontSize: 11, fill: '#64748b' }}
                />
                <Tooltip 
                  contentStyle={{ 
                    backgroundColor: '#fff',
                    border: '1px solid #e2e8f0',
                    borderRadius: '8px'
                  }}
                />
                <Bar 
                  dataKey="cantidad" 
                  radius={[4, 4, 0, 0]}
                  fill="#3b82f6"
                >
                  {chartData.bySeverity.map((entry) => (
                    <rect 
                      key={entry.severidad}
                      fill={SEVERITY_COLORS_HEX[entry.severidad] || '#64748b'}
                    />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Card>
        )}

        {/* Mapa de Calor Geográfico */}
        {isWidgetVisible('geo_heatmap') && (
          <GeoHeatmap embedded={true} />
        )}
      </div>

      {/* Segunda fila de gráficos */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        {/* Alertas por Monitor */}
        {isWidgetVisible('chart_alertas_monitor') && (
        <Card className="p-6 bg-white border-slate-200">
          <h3 className="text-lg font-semibold text-slate-900 mb-4">
            Top Alertas por Monitor
          </h3>
          <div className="h-64">
            {chartData.byMonitor.length > 0 ? (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={chartData.byMonitor} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" horizontal={true} vertical={false} />
                  <XAxis type="number" tick={{ fontSize: 11, fill: '#64748b' }} />
                  <YAxis 
                    type="category" 
                    dataKey="monitor" 
                    tick={{ fontSize: 10, fill: '#64748b' }}
                    width={120}
                  />
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: '#fff',
                      border: '1px solid #e2e8f0',
                      borderRadius: '8px'
                    }}
                    formatter={(value, name) => {
                      const labels = { total: 'Total', criticas: 'Críticas', altas: 'Altas', medias: 'Medias', bajas: 'Bajas' };
                      return [value, labels[name] || name];
                    }}
                  />
                  <Legend />
                  <Bar dataKey="total" fill="#3b82f6" name="Total" radius={[0, 4, 4, 0]}>
                    {chartData.byMonitor.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={MONITOR_COLORS[index % MONITOR_COLORS.length]} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="flex items-center justify-center h-full text-slate-400">
                No hay datos de monitores
              </div>
            )}
          </div>
        </Card>
        )}

        {/* Calendario Mini */}
        {isWidgetVisible('calendario_mini') && (
        <Card className="p-6 bg-white border-slate-200">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-slate-900 flex items-center gap-2">
              <Calendar size={20} className="text-blue-600" />
              Calendario de Alertas
            </h3>
            <div className="flex items-center gap-1">
              <Button variant="ghost" size="sm" onClick={handleCalendarPrevMonth}>
                <ChevronLeft size={16} />
              </Button>
              <span className="text-sm font-medium min-w-[120px] text-center">
                {calendarDate.toLocaleDateString('es-ES', { month: 'long', year: 'numeric' })}
              </span>
              <Button variant="ghost" size="sm" onClick={handleCalendarNextMonth}>
                <ChevronRight size={16} />
              </Button>
            </div>
          </div>
          <MiniCalendar date={calendarDate} eventos={calendarData.eventos} />
        </Card>
        )}
      </div>

      {/* Widget de Aprobaciones Pendientes */}
      {isWidgetVisible('aprobaciones_pendientes') && (
        <Card className="p-6 bg-white border-slate-200 mb-8" data-testid="pending-approvals-widget">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-slate-900 flex items-center gap-2">
              <ClipboardCheck size={20} className="text-purple-600" />
              Aprobaciones Pendientes
              {pendingApprovalCount > 0 && (
                <span className="ml-2 px-2 py-0.5 text-xs font-bold rounded-full bg-purple-100 text-purple-700">
                  {pendingApprovalCount}
                </span>
              )}
            </h3>
            <Button 
              variant="outline" 
              size="sm"
              onClick={() => navigate('/m/aprobaciones')}
              data-testid="view-all-approvals-btn"
            >
              Ver todas
            </Button>
          </div>
          
          <div className="space-y-3">
            {pendingApprovals.length === 0 ? (
              <div className="text-center py-8 text-slate-400">
                <ClipboardCheck size={40} className="mx-auto mb-2 opacity-50" />
                <p className="text-sm">No tienes aprobaciones pendientes</p>
              </div>
            ) : (
              pendingApprovals.map((approval) => (
                <div
                  key={approval.id}
                  className="flex items-center justify-between p-4 rounded-lg border border-slate-200 bg-slate-50 hover:bg-purple-50 cursor-pointer transition-colors"
                  onClick={() => navigate(`/m/aprobaciones`)}
                  data-testid={`approval-item-${approval.id}`}
                >
                  <div className="flex items-center space-x-4">
                    <span className={`inline-flex items-center rounded-md px-2.5 py-1 text-xs font-semibold ${
                      approval.prioridad === 'CRITICA' ? 'bg-red-100 text-red-800' :
                      approval.prioridad === 'ALTA' ? 'bg-orange-100 text-orange-800' :
                      approval.prioridad === 'MEDIA' ? 'bg-yellow-100 text-yellow-800' :
                      'bg-green-100 text-green-800'
                    }`}>
                      {approval.prioridad}
                    </span>
                    <div>
                      <p className="font-medium text-slate-900 text-sm">{approval.entidad_nombre}</p>
                      <p className="text-xs text-slate-500 flex items-center gap-1">
                        <Clock size={12} />
                        {new Date(approval.fecha_creacion).toLocaleDateString('es-ES')} • {approval.tipo}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-medium px-2 py-1 rounded bg-purple-100 text-purple-700">
                      {approval.accion?.replace('CAMBIAR_ESTADO:', '').replace('→', ' → ') || approval.accion}
                    </span>
                    <ArrowRight size={16} className="text-slate-400" />
                  </div>
                </div>
              ))
            )}
          </div>
        </Card>
      )}

      {/* Widget de Solicitudes de Onboarding */}
      {isWidgetVisible('solicitudes_onboarding') && (
        <Card className="p-6 bg-white border-slate-200 mb-8" data-testid="onboarding-widget">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-slate-900 flex items-center gap-2">
              <Building2 size={20} className="text-blue-600" />
              Solicitudes de Onboarding
              {(onboardingCounts.ENVIADO || 0) + (onboardingCounts.EN_REVISION || 0) > 0 && (
                <span className="ml-2 px-2 py-0.5 text-xs font-bold rounded-full bg-blue-100 text-blue-700">
                  {(onboardingCounts.ENVIADO || 0) + (onboardingCounts.EN_REVISION || 0)}
                </span>
              )}
            </h3>
            <Button 
              variant="outline" 
              size="sm"
              onClick={() => navigate('/m/empresas/solicitudes-onboarding')}
              data-testid="view-all-onboarding-btn"
            >
              Ver todas
            </Button>
          </div>
          
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div 
              className="p-4 bg-purple-50 rounded-lg cursor-pointer hover:bg-purple-100 transition-colors"
              onClick={() => navigate('/m/empresas/solicitudes-onboarding?estado=ENVIADO')}
            >
              <div className="flex items-center gap-2 mb-1">
                <UserPlus size={16} className="text-purple-600" />
                <span className="text-xs font-medium text-purple-600">Enviadas</span>
              </div>
              <p className="text-2xl font-bold text-purple-900">{onboardingCounts.ENVIADO || 0}</p>
            </div>
            
            <div 
              className="p-4 bg-amber-50 rounded-lg cursor-pointer hover:bg-amber-100 transition-colors"
              onClick={() => navigate('/m/empresas/solicitudes-onboarding?estado=EN_REVISION')}
            >
              <div className="flex items-center gap-2 mb-1">
                <Eye size={16} className="text-amber-600" />
                <span className="text-xs font-medium text-amber-600">En Revisión</span>
              </div>
              <p className="text-2xl font-bold text-amber-900">{onboardingCounts.EN_REVISION || 0}</p>
            </div>
            
            <div 
              className="p-4 bg-blue-50 rounded-lg cursor-pointer hover:bg-blue-100 transition-colors"
              onClick={() => navigate('/m/empresas/solicitudes-onboarding?estado=EN_PROGRESO')}
            >
              <div className="flex items-center gap-2 mb-1">
                <Clock size={16} className="text-blue-600" />
                <span className="text-xs font-medium text-blue-600">En Progreso</span>
              </div>
              <p className="text-2xl font-bold text-blue-900">{onboardingCounts.EN_PROGRESO || 0}</p>
            </div>
            
            <div 
              className="p-4 bg-green-50 rounded-lg cursor-pointer hover:bg-green-100 transition-colors"
              onClick={() => navigate('/m/empresas/solicitudes-onboarding?estado=APROBADO')}
            >
              <div className="flex items-center gap-2 mb-1">
                <CheckCircle size={16} className="text-green-600" />
                <span className="text-xs font-medium text-green-600">Aprobadas</span>
              </div>
              <p className="text-2xl font-bold text-green-900">{onboardingCounts.APROBADO || 0}</p>
            </div>
          </div>
        </Card>
      )}

      {/* Widget de Última Ejecución de Monitores */}
      {isWidgetVisible('ultimas_ejecuciones') && lastExecution && (
        <Card className="p-6 bg-white border-slate-200 mb-8" data-testid="last-execution-widget">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-slate-900 flex items-center gap-2">
              <Activity size={20} className="text-green-600" />
              Última Ejecución de Monitores
              {lastExecution.total_alertas_generadas > 0 && (
                <span className="ml-2 px-2 py-0.5 text-xs font-bold rounded-full bg-red-100 text-red-700">
                  {lastExecution.total_alertas_generadas} alertas
                </span>
              )}
            </h3>
            <Button 
              variant="outline" 
              size="sm"
              onClick={() => navigate('/m/monitoreo-transaccional/scheduler')}
              data-testid="view-all-executions-btn"
            >
              Programación
            </Button>
          </div>
          
          <div className="bg-slate-50 rounded-lg p-4 mb-4">
            <div className="flex items-center justify-between mb-2">
              <span className="font-medium text-slate-900">{lastExecution.group_nombre}</span>
              <span className="text-sm text-slate-500">
                {new Date(lastExecution.fecha_inicio).toLocaleString('es-AR', {
                  day: '2-digit',
                  month: '2-digit',
                  year: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit'
                })}
              </span>
            </div>
          </div>
          
          <div className="grid grid-cols-3 gap-4">
            <div className="text-center p-3 bg-blue-50 rounded-lg">
              <p className="text-2xl font-bold text-blue-700">{lastExecution.monitores_ejecutados}</p>
              <p className="text-xs text-blue-600">Monitores</p>
            </div>
            <div className="text-center p-3 bg-purple-50 rounded-lg">
              <p className="text-2xl font-bold text-purple-700">{lastExecution.total_coincidencias}</p>
              <p className="text-xs text-purple-600">Coincidencias</p>
            </div>
            <div className={`text-center p-3 rounded-lg ${lastExecution.total_alertas_generadas > 0 ? 'bg-red-50' : 'bg-green-50'}`}>
              <p className={`text-2xl font-bold ${lastExecution.total_alertas_generadas > 0 ? 'text-red-700' : 'text-green-700'}`}>
                {lastExecution.total_alertas_generadas}
              </p>
              <p className={`text-xs ${lastExecution.total_alertas_generadas > 0 ? 'text-red-600' : 'text-green-600'}`}>Alertas</p>
            </div>
          </div>
        </Card>
      )}

      {/* Alertas Recientes */}
      {isWidgetVisible('alertas_recientes') && (
      <Card className="p-6 bg-white border-slate-200">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-slate-900">
            Alertas Recientes
          </h3>
          <Button 
            variant="outline" 
            size="sm"
            onClick={() => navigate('/m/monitoreo-transaccional/alerts')}
          >
            Ver todas
          </Button>
        </div>
        
        <div className="space-y-3">
          {recentAlerts.length === 0 ? (
            <p className="text-slate-500 text-center py-4">No hay alertas recientes</p>
          ) : (
            recentAlerts.map((alert) => (
              <div
                key={alert.id}
                className="flex items-center justify-between p-4 rounded-lg border border-slate-200 bg-slate-50 hover:bg-slate-100 cursor-pointer transition-colors"
                onClick={() => navigate(`/m/monitoreo-transaccional/alerts/${alert.id}`)}
              >
                <div className="flex items-center space-x-4">
                  <span className={`inline-flex items-center rounded-md px-2.5 py-1 text-xs font-semibold ${getSeverityColorSoft(alert.severidad)}`}>
                    {alert.severidad}
                  </span>
                  <div>
                    <p className="font-medium text-slate-900 text-sm">{alert.titulo}</p>
                    <p className="text-xs text-slate-500">
                      {new Date(alert.created_at).toLocaleString('es-ES')}
                    </p>
                  </div>
                </div>
                <span className="text-xs font-medium px-2 py-1 rounded bg-slate-200 text-slate-700">
                  {alert.estado.replace('_', ' ')}
                </span>
              </div>
            ))
          )}
        </div>
      </Card>
      )}

      {/* Diálogo de Cierre de Período */}
      <Dialog open={showCierreDialog} onOpenChange={setShowCierreDialog}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-hidden flex flex-col bg-white">
          <DialogHeader>
            <DialogTitle className="text-slate-900 flex items-center gap-2">
              <Database className="text-teal-600" size={20} />
              Estado de Cierre de Períodos
            </DialogTitle>
            <DialogDescription>
              Gestione el cierre de datos de movimientos y padrón por período
            </DialogDescription>
          </DialogHeader>
          
          <div className="flex-1 overflow-auto py-4">
            <div className="grid gap-3">
              {cierrePeriodoData?.periodos?.map((p) => {
                const meses = ['', 'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];
                const mes = parseInt(p.periodo.substring(4, 6));
                const año = p.periodo.substring(0, 4);
                const periodoLabel = `${meses[mes]} ${año}`;
                
                return (
                  <div key={p.periodo} className="bg-slate-50 rounded-lg p-4 border border-slate-200">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div>
                          <p className="font-semibold text-slate-900">{periodoLabel}</p>
                          <p className="text-xs text-slate-500">
                            Vence: {new Date(p.fecha_vencimiento).toLocaleDateString('es-ES')}
                            {p.dias_para_vencer > 0 ? ` (${p.dias_para_vencer} días)` : 
                             p.dias_para_vencer === 0 ? ' (Hoy)' : 
                             ` (Vencido hace ${Math.abs(p.dias_para_vencer)} días)`}
                          </p>
                        </div>
                        <Badge 
                          className={
                            p.estado === 'presentado' ? 'bg-green-100 text-green-700' :
                            p.estado === 'vencido' ? 'bg-red-100 text-red-700' :
                            p.estado === 'por_vencer' ? 'bg-amber-100 text-amber-700' :
                            p.estado === 'listo' ? 'bg-blue-100 text-blue-700' :
                            'bg-slate-100 text-slate-700'
                          }
                        >
                          {p.estado === 'presentado' ? 'Presentado' :
                           p.estado === 'vencido' ? 'Vencido' :
                           p.estado === 'por_vencer' ? 'Por vencer' :
                           p.estado === 'listo' ? 'Listo para generar' :
                           p.estado === 'generado' ? 'Reporte generado' : 'Pendiente'}
                        </Badge>
                      </div>
                      
                      <div className="flex items-center gap-3">
                        <button
                          onClick={() => handleMarcarCierre(p.periodo, 'movimientos', !p.cierre.movimientos_completo)}
                          className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors flex items-center gap-2 ${
                            p.cierre.movimientos_completo 
                              ? 'bg-green-500 text-white hover:bg-green-600' 
                              : 'bg-slate-200 text-slate-700 hover:bg-slate-300'
                          }`}
                        >
                          {p.cierre.movimientos_completo ? <CheckCircle size={16} /> : <Clock size={16} />}
                          Movimientos
                        </button>
                        <button
                          onClick={() => handleMarcarCierre(p.periodo, 'padron', !p.cierre.padron_completo)}
                          className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors flex items-center gap-2 ${
                            p.cierre.padron_completo 
                              ? 'bg-green-500 text-white hover:bg-green-600' 
                              : 'bg-slate-200 text-slate-700 hover:bg-slate-300'
                          }`}
                        >
                          {p.cierre.padron_completo ? <CheckCircle size={16} /> : <Clock size={16} />}
                          Padrón
                        </button>
                        {p.reportes_generados > 0 && (
                          <Badge variant="outline" className="text-blue-600 border-blue-300">
                            {p.reportes_generados} reporte(s)
                          </Badge>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
          
          <div className="border-t pt-4 flex justify-between items-center">
            <p className="text-xs text-slate-500">
              Marque como completo cuando los datos estén listos para procesar
            </p>
            <Button variant="outline" onClick={() => setShowCierreDialog(false)}>
              Cerrar
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Dashboard;
